import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authFetch, readJSON } from '../lib/auth';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import CelebrationOverlay from '../components/CelebrationOverlay';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 8;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const razorpayPaymentId = params.get('razorpay_payment_id');
  const razorpayGranted = params.get('granted') === '1';
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ phase: 'polling', message: 'Confirming payment…' });
  const [celebration, setCelebration] = useState(null);
  const attempts = useRef(0);
  const stopped = useRef(false);

  // Once the payment is confirmed, fetch the active celebration animation
  // and play it as a one-shot overlay.
  useEffect(() => {
    if (state.phase !== 'success' || celebration !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/config/celebration');
        if (!res.ok) return;
        const cfg = await readJSON(res);
        if (cancelled) return;
        if (cfg?.enabled === false) { setCelebration('off'); return; }
        setCelebration(cfg?.animation || 'confetti');
      } catch {
        // Silent — celebration is decorative only.
      }
    })();
    return () => { cancelled = true; };
  }, [state.phase, celebration]);

  useEffect(() => {
    // Razorpay path — verification already happened in /pricing before redirect.
    if (razorpayPaymentId) {
      try { 
        localStorage.removeItem('fls_new_project_count_v1'); 
      } catch (error) {
        console.warn('Failed to clear localStorage:', error);
      }
      refresh();
      setState({
        phase: 'success',
        message: razorpayGranted
          ? 'Payment confirmed and applied to your account.'
          : 'Payment received — your account will update shortly.',
      });
      return;
    }

    if (!sessionId) {
      setState({ phase: 'error', message: 'Missing session_id in URL.' });
      return;
    }

    const poll = async () => {
      if (stopped.current) return;
      attempts.current += 1;
      try {
        const res = await authFetch(`/api/billing/checkout/status/${encodeURIComponent(sessionId)}`);
        if (!res.ok) {
          if (res.status === 401) {
            setState({ phase: 'error', message: 'Please sign in to confirm your payment.' });
            return;
          }
          throw new Error('Status check failed');
        }
        const data = await readJSON(res);
        if (data.payment_status === 'paid') {
          stopped.current = true;
          await refresh();
          // Reset the free-tier "New Project" counter so paid users start clean.
          try { 
            localStorage.removeItem('fls_new_project_count_v1'); 
          } catch (error) {
            console.warn('Failed to clear localStorage:', error);
          }
          setState({
            phase: 'success',
            message: data.granted_now
              ? 'Payment confirmed and applied to your account.'
              : 'Payment already applied to your account.',
            packageId: data.package_id,
          });
          return;
        }
        if (data.status === 'expired') {
          setState({ phase: 'error', message: 'Payment session expired. Please try again.' });
          return;
        }
        if (attempts.current >= MAX_POLL_ATTEMPTS) {
          setState({
            phase: 'pending',
            message:
              'Payment is still processing. We\'ll auto-apply it to your account once Stripe confirms — you can return to the studio.',
          });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ phase: 'error', message: e.message || 'Could not check payment status.' });
      }
    };

    poll();
    return () => { stopped.current = true; };
  }, [sessionId, refresh, razorpayPaymentId, razorpayGranted]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white p-4">
      {celebration && celebration !== 'off' && (
        <CelebrationOverlay animation={celebration} onDone={() => setCelebration('off')} />
      )}
      <div className="w-full max-w-md bg-slate-800/60 backdrop-blur-md border border-slate-700/60 rounded-2xl shadow-2xl p-8 text-center">
        {state.phase === 'polling' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-amber-400 animate-spin" />
            <h1 className="text-xl font-bold mb-1">Confirming your payment</h1>
            <p className="text-sm text-slate-400">{state.message}</p>
          </>
        )}
        {state.phase === 'success' && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4 text-emerald-400" />
            <h1 className="text-2xl font-bold mb-1">You're all set</h1>
            <p className="text-sm text-slate-400 mb-6">{state.message}</p>
            <button
              onClick={() => navigate('/')}
              data-testid="billing-success-continue"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold px-5 py-2.5 rounded-lg shadow"
            >
              Back to studio <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {state.phase === 'pending' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-amber-400 animate-spin" />
            <h1 className="text-xl font-bold mb-1">Still processing</h1>
            <p className="text-sm text-slate-400 mb-6">{state.message}</p>
            <Link to="/" className="text-sm text-amber-400 hover:underline">Return to studio</Link>
          </>
        )}
        {state.phase === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-rose-400" />
            <h1 className="text-xl font-bold mb-1">Something went wrong</h1>
            <p className="text-sm text-slate-400 mb-6">{state.message}</p>
            <Link to="/pricing" className="inline-block text-sm text-amber-400 hover:underline">Back to pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
